import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { Accordion, AccordionSummary, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import NumberFormat from "react-number-format";
import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import BonusDistribution from "./BonusDistribution";
import Buy from "./Buy";
import Campaign from "./Campaign";
import CoolingOff from "./CoolingOff";
import Dividend from "./Dividend";
import Sell from "./Sell";
import SwitchTrx from "./Switch";
import Transfer from "./Transfer";
import WithoutOrderIdSwitchOut from "./WithoutOrderIdSwitchOut";
import WithoutOrderIdSwitchIn from "./WithoutOrderIdSwitchIn";
import WithoutOrderIdSell from "./WithoutOrderIdSell";
import WithoutOrderIdBuy from "./WithoutOrderIdBuy";
import styles from "./styles";
import {
  decideAmount,
  getRefundStatusText,
  getStatus,
  getTransactionType,
} from "./utils";
import BonusUnit from "./BonusUnit";

const StyledExpansionPanel = styled(Accordion)(() => ({
  border: 0,
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
}));

const StyledExpansionPanelSummary  = styled(AccordionSummary)(() => ({
  margin: 0,
  padding: "0 16px",
  display: "flex",
  alignItems: "flex-start",

  "& .MuiAccordionSummary-content": {
    gap: "15px",
    backgroundColor: "#fff",
    borderRadius: "4px",
    justifyContent: "space-between",
    "&.Mui-expanded": {
      margin: "16px 0",
    },
    "&.MuiAccordionSummary-content": {
      gap: 0,
    },
  },

  "& .MuiAccordionSummary-expandIconWrapper": {
    marginLeft: "8px",
    padding: 0,
    marginTop: "12px",
    "&.Mui-expanded": {
      marginTop: "14px",
    },
  },
}));


const getRefundName = val => {
  if (Array.isArray(val) && val.length > 0) {
    let refundName = "-";
    const switchOutDetail = val.find(detail => detail.type === "SwitchOut");
    const switchAllDetail = val.find(detail => detail.type === "SwitchAll");

    if (switchOutDetail) {
      refundName = switchOutDetail.planAlias;
    }
    if (switchAllDetail) {
      refundName = switchAllDetail.planAlias;
    }
    return refundName;
  }
};

const getExpansionSummary = ({
  amount,
  submittedDate,
  processingDate,
  completedDate,
  method,
  fee,
  unit,
  status,
  details,
  coolOffAmt,
  salesChargeAmount,
  salesChargeCcy,
  salesChargePercent,
  cashOutamtSell,
  goalName,
  planId,
  actualAmount,
  amountOnly,
  ppaFee,
  transactionType,
  cashoutAmount,
  compUnitPrice
}) => {
  return {
    CorrBuy: (
      <WithoutOrderIdBuy
        status={status}
        amount={amount}
        unit={unit}
        completedDate={completedDate}
        details={details}
        salesChargeAmount={salesChargeAmount}
        salesChargePercent={salesChargePercent}
        type={transactionType}
      />
    ),
    CorrSell: (
      <WithoutOrderIdSell
        status={status}
        amount={amount}
        unit={unit}
        completedDate={completedDate}
        details={details}
      />
    ),
    CorrSWI: (
      <WithoutOrderIdSwitchIn
        status={status}
        amountOnly={amountOnly}
        actAmount={actualAmount}
        unit={unit}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargePercent={salesChargePercent}
        details={details}
        compUnitPrice={compUnitPrice}
        type={transactionType}
      />
    ),
    CorrSWO: (
      <WithoutOrderIdSwitchOut
        status={status}
        amountOnly={amountOnly}
        actAmount={actualAmount}
        salesChargeAmount={salesChargeAmount}
        unit={unit}
        completedDate={completedDate}
        details={details}
        compUnitPrice={compUnitPrice}
      />
    ),
    Dividend: (
      <Dividend
        status={status}
        amount={amount}
        unit={unit}
        completedDate={completedDate}
        details={details}
      />
    ),
    Bonus: (
      <BonusDistribution
        status={status}
        amount={amount}
        unit={unit}
        completedDate={completedDate}
        details={details}
      />
    ),
    CoolOff: (
      <CoolingOff
        status={status}
        method={method}
        amount={amount}
        coolOffAmt={coolOffAmt}
        fee={fee}
        unit={unit}
        completedDate={completedDate}
        submittedDate={submittedDate}
        details={details}
      />
    ),
    Sell: (
      <Sell
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        details={details}
        cashOutamtSell={cashOutamtSell}
        // retire only
        planId={planId}
        //
        amountOnly={amountOnly}
        cashoutAmount={cashoutAmount}
        isSell
      />
    ),
    ManSell: (
      <Sell
        status={status}
        method={method}
        amount={amount}
        amountOnly={amountOnly}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        details={details}
        cashOutamtSell={cashOutamtSell}
        compUnitPrice={compUnitPrice}
        isManSell
      />
    ),
    ManFullSell: (
      <Sell
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        details={details}
        cashOutamtSell={cashOutamtSell}
        isManSell
        compUnitPrice={compUnitPrice}
        // retire only
        planId={planId}
        amountOnly={amountOnly}
      />
    ),
    FullSell: (
      <Sell
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        details={details}
        cashOutamtSell={cashOutamtSell}
        isFullSell
        // retire only
        planId={planId}
        amountOnly={amountOnly}
      />
    ),
    Campaign: (
      <Campaign
        status={status}
        amount={amount}
        unit={unit}
        completedDate={processingDate}
        details={details}
      />
    ),
    Buy: (
      <Buy
        status={status}
        method={method}
        amount={amount}
        actualAmount={actualAmount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        planId={planId}
        amountOnly={amountOnly}
        ppaFee={ppaFee}
      />
    ),
    ManBuy: (
      <Buy
        status={status}
        method={method}
        amount={amount}
        actualAmount={actualAmount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        planId={planId}
        amountOnly={amountOnly}
        ppaFee={ppaFee}
        transactionType="ManBuy"
        isManBuy
      />
    ),
    RSPBuy: (
      <Buy
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        details={details}
        isRsp
      />
    ),
    TransferOut: (
      <Transfer
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
      />
    ),
    TransferIn: (
      <Transfer
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        isTransferIn
      />
    ),
    SwitchIn: (
      <SwitchTrx
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        isSwitchIn
        planName={getRefundName(details)}
        transactionType={transactionType}
      />
    ),
    SwitchOut: (
      <SwitchTrx
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        planName={goalName}
        transactionType={transactionType}
        isSwitchOut
      />
    ),
    ManSwitchIn: (
      <SwitchTrx
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        isSwitchIn
        planName={getRefundName(details)}
        transactionType={transactionType}
        isManSwitchIn
      />
    ),
    ManSwitchOut: (
      <SwitchTrx
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        planName={goalName}
        transactionType={transactionType}
        isSwitchOut
        isManSwitchOut
      />
    ),
    ManSwitchAll: (
      <SwitchTrx
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        planName={goalName}
        transactionType={transactionType}
        isManSwitchAll
      />
    ),

    SwitchAll: (
      <SwitchTrx
        status={status}
        method={method}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        planName={goalName}
        transactionType={transactionType}
        isSwitchAll
      />
    ),

    BonusUnits: (
      <BonusUnit
        status={status}
        amount={amount}
        fee={fee}
        unit={unit}
        submittedDate={submittedDate}
        processingDate={processingDate}
        completedDate={completedDate}
        salesChargeAmount={salesChargeAmount}
        salesChargeCcy={salesChargeCcy}
        salesChargePercent={salesChargePercent}
        details={details}
        transactionType={transactionType}

      />
    )
  };
};

const TransactionHistoryDate = ({
  transactionType,
  goalName,
  amount,
  submittedDate,
  processingDate,
  completedDate,
  method,
  fee,
  unit,
  compUnitPrice,
  status,
  details,
  coolOffAmt,
  actualAmount,
  salesChargeAmount,
  salesChargeCcy,
  salesChargePercent,
  cashOutamtSell,
  planId,
  amountOnly,
  ppaFee,
  cashoutAmount,
}) => {
  const isPlusTransaction =
    (transactionType === "Dividend" && Number(amount) > 0) ||
    transactionType === "Bonus" ||
    transactionType === "Buy" ||
    transactionType === "ManBuy" ||
    transactionType === "RSPBuy" ||
    transactionType === "Campaign" ||
    transactionType === "TransferIn" ||
    transactionType === "SwitchIn" ||
    transactionType === "ManSwitchIn" ||
    transactionType === "CorrSWI" ||
    transactionType === "CorrBuy" ||
    transactionType === "BonusUnits";

  const isGreenIsh =
    transactionType === "Buy" ||
    transactionType === "ManBuy" ||
    transactionType === "RSPBuy" ||
    transactionType === "TransferIn" ||
    transactionType === "SwitchIn" ||
    transactionType === "ManSwitchIn" ||
    transactionType === "CorrSWI" ||
    transactionType === "CorrBuy";

  const isBlueIsh =
    transactionType === "Dividend" ||
    transactionType === "Bonus" ||
    transactionType === "Campaign" ||
    transactionType === "BonusUnits";

  const isSwitch =
    transactionType === "SwitchIn" ||
    transactionType === "SwitchOut" ||
    transactionType === "SwitchAll" ||
    transactionType === "ManSwitchIn" ||
    transactionType === "ManSwitchOut";

  const totalTransactionInRinggit = `${isPlusTransaction ? "+" : "-"} RM`;
  const isFailedSwitchIn =
    (transactionType === "SwitchIn" || transactionType === "ManSwitchIn") &&
    status === "9";
  const expansionSummary = getExpansionSummary({
    amount,
    submittedDate,
    processingDate,
    completedDate,
    method,
    fee,
    unit,
    status,
    details,
    coolOffAmt,
    salesChargeAmount,
    salesChargeCcy,
    salesChargePercent,
    cashOutamtSell,
    goalName,
    planId,
    actualAmount,
    amountOnly,
    ppaFee,
    transactionType,
    cashoutAmount,
    compUnitPrice
  });

  const RefundStatus = () => {
    return (
      <span style={{ ...styles.refundStatus }}>
        {getRefundStatusText(status, transactionType, details)}
      </span>
    );
  };

  return (
    <div
      style={{ marginBottom: "16px" }}
      onClick={() =>
        dataLayerGTM(
          "click_tag",
          "Click",
          "Expand",
          `Expand of ${getTransactionType(transactionType)}`
        )
      }
    >
      <div style={{ width: "100%" }}>
        <StyledExpansionPanel>
          <StyledExpansionPanelSummary
            expandIcon={
              <ExpandMoreOutlinedIcon
                sx={{ cursor: "pointer", color: "#0091da", fontSize: 24 }}
              />
            }
          >
            <div style={{ ...styles.column }}>
              <Typography
                style={{ ...styles.baseFont, ...styles.transactionType }}
              >
                {getTransactionType(transactionType)}
              </Typography>
              <Typography
                style={{ ...styles.baseFont, ...styles.transactionName }}
              >
                {goalName}
              </Typography>
            </div>
            {/* heading */}
            <div
              style={{
                textAlign: "right",
                minWidth: "150px",
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                style={{
                  ...styles.baseFont,
                  fontWeight: "700",
                  color: isGreenIsh ? "#18a29a" : isBlueIsh && "#1278CC",
                }}
              >
                {isFailedSwitchIn ? (
                  "-"
                ) : (
                  <>
                    {" "}
                    {totalTransactionInRinggit}
                    <NumberFormat
                      value={decideAmount({
                        transactionType,
                        details,
                        amount,
                        fee,
                        actualAmount,
                        cashOutamtSell,
                        status,
                        amountOnly,
                        planId,
                        salesChargeAmount,
                      })}
                      thousandSeparator
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </>
                )}
              </Typography>
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.transactionStatus,
                  color: (status === "9" || status === "11") && "#BE0712",
                }}
              >
                {getStatus({ status, transactionType, details, planId })}
              </Typography>

              {isSwitch && status === "9" && <RefundStatus />}
            </div>
          </StyledExpansionPanelSummary>
          {expansionSummary[transactionType]}
        </StyledExpansionPanel>
      </div>
    </div>
  );
};

export default TransactionHistoryDate;
